(() => {
  if (window.customElements.get('npc-product-picker')) return;

  class NpcProductPicker extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-product-picker-input]');
      this.results = this.querySelector('[data-product-picker-results]');
      this.selectedList = this.querySelector('[data-product-picker-selected]');
      this.status = this.querySelector('[data-product-picker-status]');
      this.form = this.closest('form');

      if (!this.input || !this.results || !this.form) return;

      this.selectedProducts = [];
      this.currentResults = [];
      this.activeIndex = -1;
      this.requestController = null;
      this.searchTimer = null;
      this.manualFallback = false;

      this.input.addEventListener('input', () => this.queueSearch());
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.results.addEventListener('click', (event) => this.onResultClick(event));
      this.selectedList.addEventListener('click', (event) => this.onRemoveClick(event));
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.closeResults();
      });
    }

    queueSearch() {
      window.clearTimeout(this.searchTimer);
      this.input.setCustomValidity('');
      this.status.classList.remove('is-error');

      const query = this.input.value.trim();
      if (query.length < 2) {
        this.closeResults();
        this.status.textContent = this.selectedProducts.length
          ? 'Sök igen för att lägga till fler produkter.'
          : 'Skriv minst två tecken och välj rätt produkt från listan.';
        return;
      }

      this.status.textContent = 'Söker i sortimentet…';
      this.searchTimer = window.setTimeout(() => this.search(query), 260);
    }

    async search(query) {
      if (this.requestController) this.requestController.abort();
      this.requestController = new AbortController();

      const root = window.Shopify?.routes?.root || '/';
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': '10',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'show',
        'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor'
      });

      try {
        const response = await fetch(`${root}search/suggest.json?${params.toString()}`, {
          signal: this.requestController.signal,
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) throw new Error(`Product search failed: ${response.status}`);

        const data = await response.json();
        const products = data?.resources?.results?.products || [];
        this.manualFallback = false;
        this.renderResults(products);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.manualFallback = true;
        this.closeResults();
        this.status.textContent = 'Produktsökningen kunde inte laddas. Skriv produktnamnet manuellt.';
        this.status.classList.add('is-error');
      }
    }

    renderResults(products) {
      this.results.replaceChildren();
      this.currentResults = products;
      this.activeIndex = -1;

      if (!products.length) {
        const empty = document.createElement('p');
        empty.className = 'npc-product-picker__empty';
        empty.textContent = 'Ingen produkt hittades. Kontrollera stavningen eller prova ett kortare sökord.';
        this.results.appendChild(empty);
        this.openResults();
        this.status.textContent = 'Inga träffar i sortimentet.';
        return;
      }

      products.forEach((product, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'npc-product-picker__result';
        button.dataset.productIndex = String(index);
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', 'false');

        const imageBox = document.createElement('span');
        imageBox.className = 'npc-product-picker__result-image';
        const imageUrl = product.image || product.featured_image?.url;
        if (imageUrl) {
          const image = document.createElement('img');
          image.src = imageUrl;
          image.alt = '';
          image.loading = 'lazy';
          image.width = 62;
          image.height = 62;
          imageBox.appendChild(image);
        }

        const copy = document.createElement('span');
        copy.className = 'npc-product-picker__result-copy';
        const title = document.createElement('strong');
        title.textContent = product.title;
        const meta = document.createElement('small');
        meta.textContent = [product.vendor, product.type].filter(Boolean).join(' · ') || 'Nordic Pet House';
        copy.append(title, meta);

        const action = document.createElement('span');
        action.className = 'npc-product-picker__result-action';
        action.textContent = this.isSelected(product) ? 'Vald' : 'Välj +';

        button.append(imageBox, copy, action);
        this.results.appendChild(button);
      });

      this.openResults();
      this.status.textContent = `${products.length} ${products.length === 1 ? 'produkt hittades' : 'produkter hittades'}. Välj rätt produkt.`;
    }

    onResultClick(event) {
      const button = event.target.closest('[data-product-index]');
      if (!button) return;
      const product = this.currentResults[Number(button.dataset.productIndex)];
      if (product) this.selectProduct(product);
    }

    onKeydown(event) {
      const options = [...this.results.querySelectorAll('[data-product-index]')];
      if (!options.length || this.results.hidden) {
        if (event.key === 'Escape') this.closeResults();
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.setActiveResult((this.activeIndex + 1) % options.length, options);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.setActiveResult((this.activeIndex - 1 + options.length) % options.length, options);
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        const product = this.currentResults[this.activeIndex];
        if (product) this.selectProduct(product);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.closeResults();
      }
    }

    setActiveResult(index, options) {
      options.forEach((option) => option.classList.remove('is-active'));
      this.activeIndex = index;
      options[index].classList.add('is-active');
      options[index].scrollIntoView({ block: 'nearest' });
    }

    selectProduct(product) {
      if (!this.isSelected(product)) {
        this.selectedProducts.push({
          id: String(product.id),
          title: product.title,
          vendor: product.vendor || '',
          image: product.image || product.featured_image?.url || '',
          url: this.cleanProductUrl(product.url)
        });
      }

      this.renderSelected();
      this.input.value = '';
      this.input.setCustomValidity('');
      this.closeResults();
      this.status.classList.remove('is-error');
      this.status.textContent = 'Produkten är vald. Sök igen om du vill lägga till fler.';
      this.input.focus();
    }

    onRemoveClick(event) {
      const button = event.target.closest('[data-remove-product]');
      if (!button) return;
      this.selectedProducts = this.selectedProducts.filter((product) => product.id !== button.dataset.removeProduct);
      this.renderSelected();
      this.status.textContent = this.selectedProducts.length
        ? 'Sök igen om du vill lägga till fler produkter.'
        : 'Skriv minst två tecken och välj rätt produkt från listan.';
      this.input.focus();
    }

    renderSelected() {
      this.selectedList.replaceChildren();

      this.selectedProducts.forEach((product) => {
        const item = document.createElement('div');
        item.className = 'npc-product-picker__selected-item';

        if (product.image) {
          const image = document.createElement('img');
          image.className = 'npc-product-picker__selected-image';
          image.src = product.image;
          image.alt = '';
          image.loading = 'lazy';
          item.appendChild(image);
        } else {
          const imagePlaceholder = document.createElement('span');
          imagePlaceholder.className = 'npc-product-picker__selected-image';
          item.appendChild(imagePlaceholder);
        }

        const copy = document.createElement('span');
        copy.className = 'npc-product-picker__selected-copy';
        const title = document.createElement('strong');
        title.textContent = product.title;
        const meta = document.createElement('span');
        meta.textContent = product.vendor || 'Vald produkt';
        copy.append(title, meta);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'npc-product-picker__remove';
        remove.dataset.removeProduct = product.id;
        remove.setAttribute('aria-label', `Ta bort ${product.title}`);
        remove.textContent = '×';

        item.append(copy, remove);
        this.selectedList.appendChild(item);
      });
    }

    onSubmit(event) {
      if (this.selectedProducts.length) {
        this.input.value = this.selectedProducts
          .map((product) => `${product.title} — ${window.location.origin}${product.url}`)
          .join(' | ');
        this.input.setCustomValidity('');
        return;
      }

      if (this.manualFallback && this.input.value.trim()) {
        this.input.setCustomValidity('');
        return;
      }

      event.preventDefault();
      this.input.setCustomValidity('Sök efter och välj minst en produkt från listan.');
      this.input.reportValidity();
      this.status.textContent = 'Välj en produkt från sökresultatet innan du skickar ärendet.';
      this.status.classList.add('is-error');
      this.input.focus();
    }

    isSelected(product) {
      return this.selectedProducts.some((selected) => selected.id === String(product.id));
    }

    cleanProductUrl(rawUrl) {
      const url = new URL(rawUrl, window.location.origin);
      const variant = url.searchParams.get('variant');
      return `${url.pathname}${variant ? `?variant=${encodeURIComponent(variant)}` : ''}`;
    }

    openResults() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    closeResults() {
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.activeIndex = -1;
    }
  }

  window.customElements.define('npc-product-picker', NpcProductPicker);
})();
